import type { ShortCircuitStudyResponse } from "../services/fault";
import { StatusIcon } from "./StatusIcon";
import type { StatusTone } from "./statusTone";

type FaultEngineeringWarning = ShortCircuitStudyResponse["warnings"][number];
type WarningCode = FaultEngineeringWarning["code"];
type WarningSeverity = FaultEngineeringWarning["severity"];

interface FaultWarningPanelProps {
  warnings: FaultEngineeringWarning[];
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive maps: a new code or severity without a label is a compile error.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  NO_FAULT_CURRENT_PATH: "No fault current path",
  ZERO_SEQUENCE_PATH_BLOCKED: "Zero-sequence path blocked",
  INCOMPLETE_SEQUENCE_DATA: "Incomplete sequence data",
  CURRENT_INJECTION_APPROXIMATION: "Current-injection source approximated",
  PEAK_CURRENT_NOT_EVALUATED: "Peak current not evaluated",
  PEAK_FACTOR_METHOD_C: "Multiple-fed fault: peak factor by the equivalent frequency method",
  MINIMUM_CASE_SOURCES_NEGLECTED: "Sources neglected in the minimum case",
  MINIMUM_CASE_TEMPERATURE_NOT_DECLARED:
    "Conductor end temperature not declared for the minimum case",
  IMPEDANCE_CORRECTION_NOT_APPLIED: "No impedance correction factor applied",
  VOLTAGE_FACTOR_NOT_IN_TABLE_1: "Voltage factor not a Table 1 value",
  BREAKING_CURRENT_NOT_EVALUATED: "Breaking current not evaluated",
  STEADY_STATE_CURRENT_NOT_EVALUATED: "Steady-state current not evaluated",
  THERMAL_CURRENT_NOT_EVALUATED: "Thermal equivalent current not evaluated",
  ENGINEERING_REVIEW_REQUIRED: "Engineering review required",
  GOVERNING_REFERENCE_NOT_ESTABLISHED:
    "Governing references not registered for this jurisdiction profile",
  GOVERNING_REFERENCE_OVERRIDDEN:
    "Governing references overridden by the project (deviation from profile)",
  CALCULATION_FAILED: "Calculation failed",
};

const SEVERITY_LABELS: Record<WarningSeverity, string> = {
  WARNING: "Warning",
  ERROR: "Error",
};

// The tone of each warning (A25 (d)): an ERROR of the engine is a failed study; a review
// the engineer owes (a review flag, a reference not established) is the review tone; the
// rest are plain warnings. The severity attribute keeps the engine's own words.
function toneOf(warning: FaultEngineeringWarning): StatusTone {
  if (warning.severity === "ERROR") {
    return "fail";
  }
  return warning.code === "ENGINEERING_REVIEW_REQUIRED" ||
    warning.code === "GOVERNING_REFERENCE_NOT_ESTABLISHED"
    ? "review"
    : "warning";
}

export function FaultWarningPanel({ warnings }: FaultWarningPanelProps) {
  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <ul data-warning-count={warnings.length}>
          {warnings.map((warning, index) => (
            <li
              key={`${warning.code}-${index}`}
              data-warning-code={warning.code}
              data-warning-severity={warning.severity}
              data-warning-tone={toneOf(warning)}
            >
              <strong>
                <StatusIcon tone={toneOf(warning)} size={16} />
                {SEVERITY_LABELS[warning.severity]}
                {" - "}
                {WARNING_CODE_LABELS[warning.code]}
              </strong>
              {": "}
              <span>{warning.message}</span>
              {warning.reference_code ? (
                <>
                  {" "}
                  <small data-warning-reference={warning.reference_code}>
                    (Reference: <code>{warning.reference_code}</code>)
                  </small>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export { toneOf };

export default FaultWarningPanel;
